// src/services/apaleo/maintenance.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { MaintenanceModel } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, MockData } from './config';

const part: ThisType<ApaleoClient> = {
async fetchMaintenances(
  propertyId: string,
  from: string | null = null,
  to: string | null = null,
  limit: number = 500,
): Promise<MaintenanceModel[]> {
  if (IS_DEV) return MockData.MOCK_MAINTENANCES;
  try {
    if (!from || !to) {
      const url = `/operations/v1/maintenances?propertyId=${propertyId}&limit=${limit}`;
      const data = (await this._fetch(url)) as
        | { maintenances?: MaintenanceModel[] }
        | null;
      return data?.maintenances || [];
    }

    // Chunked range fetch (Sperrapartments snapshot-sync incident
    // 2026-10-16): a single request over a wide window truncates at the
    // per-request limit — BERLIN has >500 maintenance records in ~16
    // months, so recent windows silently fell outside the slice while
    // low-volume properties reached into the future. Splitting into
    // 45-day chunks keeps every chunk far below the limit; windows that
    // span a chunk boundary appear in both chunks and are deduped by id.
    const CHUNK_DAYS = 45;
    const MAX_CHUNKS = 60; // 45 × 60 = 2700 days — far beyond any caller
    const byId = new Map<string, MaintenanceModel>();
    const rangeStart = new Date(`${from.split("T")[0]}T00:00:00Z`);
    const rangeEnd = new Date(`${to.split("T")[0]}T23:59:59Z`);
    let cursor = new Date(rangeStart.getTime());
    let guard = 0;
    while (cursor <= rangeEnd && guard < MAX_CHUNKS) {
      guard++;
      const chunkEnd = new Date(
        Math.min(cursor.getTime() + (CHUNK_DAYS - 1) * 86400000, rangeEnd.getTime())
      );
      const cFrom = cursor.toISOString().slice(0, 10);
      const cTo = chunkEnd.toISOString().slice(0, 10);
      const url =
        `/operations/v1/maintenances?propertyId=${propertyId}` +
        `&from=${cFrom}T00:00:00Z&to=${cTo}T23:59:59Z&limit=${limit}`;
      const data = (await this._fetch(url)) as
        | { maintenances?: MaintenanceModel[] }
        | null;
      (data?.maintenances || []).forEach((m) => {
        if (m && (m as { id?: unknown }).id !== undefined) {
          byId.set(String((m as { id: unknown }).id), m);
        }
      });
      cursor = new Date(chunkEnd.getTime() + 1); // next day 00:00:00Z
    }
    return Array.from(byId.values());
  } catch (error) {
    console.error("Fehler beim Laden der Wartungsfenster:", error);
    return [];
  }
},

async createMaintenance(payload: Record<string, unknown>): Promise<unknown> {
  if (IS_DEV) return { id: "MOCK-MAINT-ID" };
  try {
    return await this._fetch("/operations/v1/maintenances", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("Fehler beim Erstellen des Wartungsfensters:", error);
    throw error;
  }
},

async updateMaintenance(
  id: string,
  payload: Record<string, unknown>[],
): Promise<unknown> {
  if (IS_DEV) return true;
  try {
    return await this._fetch(`/operations/v1/maintenances/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Wartungsfensters:", error);
    throw error;
  }
},

async deleteMaintenance(id: string): Promise<unknown> {
  if (IS_DEV) return true;
  try {
    return await this._fetch(`/operations/v1/maintenances/${id}`, {
      method: "DELETE",
    });
  } catch (error) {
    console.error("Fehler beim Löschen des Wartungsfensters:", error);
    throw error;
  }
},

};

export default part;
